import React, { useState, useEffect } from 'react';
import AssetModalWrapper from './AssetModal.styles';
import IntlMessages from '@iso/components/utility/intlMessages';
import { Button, Form, Select, Row, Col, message, Spin } from 'antd';
import { sensorService } from '@iso/services';

const { Option } = Select;

const AddSensorModal = (props) => {
    const [form] = Form.useForm();
    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sensors, setSensors] = useState([]);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalItems, setTotalItems] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    useEffect(() => {
    let isMounted = true;

    setFields([]);
    resetPagination();

    // call getSensors safely
    getSensorsSafe();

    return () => {
        isMounted = false;
    };

    function getSensorsSafe() {
        setLoading(true);

        const params = {
        property_id: props.propertyId,
        page: 1,
        pageSize: pageSize,
        };

        sensorService.getList(params)
        .then(res => {
            if (!isMounted) return; // prevent state update after unmount

            if (res.code === '0000') {
            setSensors(res.sensors || []);
            setTotalItems(res.total_item || 0);
            setCurrentPage(parseInt(res.current_page) || 1);

            const totalPages = res.total_page || Math.ceil((res.total_item || 0) / pageSize);
            setHasMore(1 < totalPages);
            }

            setLoading(false);
        })
        .catch(error => {
            if (!isMounted) return;
            console.error('Error fetching sensors:', error);
            setLoading(false);
        });
    }
    }, [props.propertyId]);

    const resetPagination = () => {
        setCurrentPage(1);
        setSensors([]);
        setTotalItems(0);
        setHasMore(true);
        setSearchValue('');
    };

    const getSensors = (page = 1, search = '', append = false) => {
        const isLoadingMore = append && page > 1;
        
        if (isLoadingMore) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }

        const params = {
            property_id: props.propertyId,
            page: page,
            pageSize: pageSize
        };

        if (search) {
            params.search = search;
        }

        sensorService.getList(params).then(res => {
            if (res.code === '0000') {
                const newSensors = res.sensors || [];
                
                if (append) {
                    setSensors(prevSensors => [...prevSensors, ...newSensors]);
                } else {
                    setSensors(newSensors);
                }

                setTotalItems(res.total_item || 0);
                setCurrentPage(parseInt(res.current_page) || page);
                
                // Check if there are more items to load
                const totalPages = res.total_page || Math.ceil((res.total_item || 0) / pageSize);
                setHasMore(page < totalPages);
            }

            setLoading(false);
            setLoadingMore(false);
        }).catch(error => {
            console.error('Error fetching sensors:', error);
            setLoading(false);
            setLoadingMore(false);
        });
    };

    const loadMoreSensors = () => {
        if (!loadingMore && hasMore) {
            const nextPage = currentPage + 1;
            getSensors(nextPage, searchValue, true);
        }
    };

    const searchTimeoutRef = React.useRef();

    const handleSearch = (value) => {
    setSearchValue(value);
    setCurrentPage(1);
    setSensors([]);
    setHasMore(true);

    if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
        getSensors(1, value, false);
    }, 300);
    };

    useEffect(() => {
    return () => clearTimeout(searchTimeoutRef.current); // cleanup on unmount
    }, []);

    const renderSensorOptions = () => {
        const options = sensors.map((sensor) => (
            <Option key={sensor.id} value={sensor.id}>
                {sensor.name} - Device: {sensor.device?.terminal_id}
            </Option>
        ));

        return (
            <Select
                placeholder="Select sensor"
                allowClear
                size="large"
                showSearch
                filterOption={false} // Disable client-side filtering since we're using server-side search
                onSearch={handleSearch}
                onPopupScroll={(e) => {
                    const { target } = e;
                    // Check if scrolled to bottom (with small threshold)
                    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 10) {
                        loadMoreSensors();
                    }
                }}
                notFoundContent={loading ? <Spin size="small" /> : 'No sensors found'}
                dropdownRender={(menu) => (
                    <>
                        {menu}
                        {loadingMore && (
                            <div style={{ padding: '8px', textAlign: 'center' }}>
                                <Spin size="small" />
                                <span style={{ marginLeft: '8px' }}>Loading more...</span>
                            </div>
                        )}
                        {!hasMore && sensors.length > 0 && (
                            <div style={{ padding: '8px', textAlign: 'center', color: '#999' }}>
                                No more sensors to load
                            </div>
                        )}
                    </>
                )}
            >
                {options}
            </Select>
        );
    };

    const handleSubmit = () => {
        setLoading(true);

        form.validateFields()
            .then(values => {
                onStore(values);
            })
            .catch(info => {
                console.log('Validate Failed:', info);
                setLoading(false);
            });
    };

    const onStore = (values) => {
    let isMounted = true;

    setLoading(true);

    sensorService.addSensorToAsset(values.sensor_id, props.assetId)
        .then(res => {
        if (!isMounted) return;

        if (res.code === '0000') {
            form.resetFields();
            props.onSaved();
        } else {
            message.error('Failed to add sensor to asset');
        }

        setLoading(false);
        })
        .catch(error => {
        if (!isMounted) return;
        console.error('Error adding sensor to asset:', error);
        message.error('Failed to add sensor to asset');
        setLoading(false);
        });

    return () => { isMounted = false; };
    };

    return (
        <AssetModalWrapper
            visible={props.visible}
            onCancel={props.onCancel}
            title={<IntlMessages id="propertyPage.mobModal.sensorTab.addSensorModal.title" />}
            footer={[
                <Button key="back" onClick={props.onCancel} className="cancel-button">
                    {<IntlMessages id="propertyPage.modal.cancel" />}
                </Button>,
                <Button key="submit" className="btn-success" type="primary" onClick={handleSubmit} loading={loading}>
                    {<IntlMessages id="propertyPage.modal.save" />}
                </Button>,
            ]}
        >
            <Form
                form={form}
                layout="vertical"
                scrollToFirstError
                fields={fields}
                id="#1"
            >
                <div className="form-body">
                    <Row>
                        <Col xs={{span: 24}} md={{span: 24}}>
                            <Form.Item
                                name="sensor_id"
                                label="Sensor"
                                rules={[
                                    {
                                        required: true,
                                        message: 'This field is required',
                                    },
                                ]}
                            >
                                {renderSensorOptions()}
                            </Form.Item>
                            
                            {/* Display total count for user reference */}
                            {totalItems > 0 && (
                                <div style={{ marginTop: '8px', color: '#666', fontSize: '12px' }}>
                                    Showing {sensors.length} of {totalItems} sensors
                                </div>
                            )}
                        </Col>
                    </Row>
                </div>
            </Form>
        </AssetModalWrapper>
    );
};

export default AddSensorModal;